import { Injectable, signal } from '@angular/core';
import { Observable, of, tap, map } from 'rxjs';
import {
  AsignacionesService,
  TicketTypeConfig,
  LEGACY_TICKET_TYPE_LABELS,
  LEGACY_TICKET_TYPE_ICONS,
  LEGACY_TICKET_TYPE_COLORS,
} from 'src/app/services/asignaciones.service';

export interface TicketTypeDisplay {
  code: string;
  label: string;
  icon: string;
  color: string;
}

@Injectable({ providedIn: 'root' })
export class TicketTypeCatalogService {
  private readonly types = signal<TicketTypeConfig[]>([]);
  private loaded = false;

  constructor(private asignaciones: AsignacionesService) {}

  /** Carga todos los tipos (todas las áreas) una vez. */
  ensureLoaded(): Observable<TicketTypeConfig[]> {
    if (this.loaded) return of(this.types());
    return this.asignaciones.getAllTicketTypes().pipe(
      tap((res) => {
        if (res?.ok && res.data) {
          this.types.set(res.data);
          this.loaded = true;
        }
      }),
      map(() => this.types()),
    );
  }

  refresh(): Observable<TicketTypeConfig[]> {
    this.loaded = false;
    return this.ensureLoaded();
  }

  allTypes(): TicketTypeConfig[] {
    return this.types();
  }

  typesForWorkarea(workareaId: number | null | undefined, activeOnly = true): TicketTypeConfig[] {
    if (!workareaId) return [];
    return this.types()
      .filter((t) => t.workareaId === workareaId && (!activeOnly || t.active))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
  }

  lookup(code: string, workareaId?: number | null): TicketTypeDisplay {
    const normalized = code?.trim();
    if (!normalized) {
      return { code: '', label: '—', icon: 'mat:task_alt', color: '#0079bf' };
    }

    if (workareaId) {
      const scoped = this.types().find((t) => t.workareaId === workareaId && t.code === normalized);
      if (scoped) {
        return { code: scoped.code, label: scoped.label, icon: scoped.icon, color: scoped.color };
      }
    }

    const anyMatch = this.types().find((t) => t.code === normalized);
    if (anyMatch) {
      return { code: anyMatch.code, label: anyMatch.label, icon: anyMatch.icon, color: anyMatch.color };
    }

    return {
      code: normalized,
      label: LEGACY_TICKET_TYPE_LABELS[normalized as keyof typeof LEGACY_TICKET_TYPE_LABELS] || normalized,
      icon: LEGACY_TICKET_TYPE_ICONS[normalized as keyof typeof LEGACY_TICKET_TYPE_ICONS] || 'mat:task_alt',
      color: LEGACY_TICKET_TYPE_COLORS[normalized as keyof typeof LEGACY_TICKET_TYPE_COLORS] || '#0079bf',
    };
  }

  filterOptions(workareaId?: number | null): TicketTypeConfig[] {
    if (workareaId) return this.typesForWorkarea(workareaId);
    const seen = new Set<string>();
    const options: TicketTypeConfig[] = [];
    for (const t of this.types()) {
      if (!t.active || seen.has(t.code)) continue;
      seen.add(t.code);
      options.push(t);
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }
}
